import { getAuthPayload } from "Common/permissions";

export interface LoganSuggestedMilestone {
  name: string;
  percentage: number;
  description: string;
}

export interface LoganProjectDraft {
  projectName: string;
  scope: string;
  homeownerFirstName: string;
  homeownerLastName: string;
  homeownerEmail: string;
  homeownerPhone: string;
  homeownerId: number | null;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  costOfProject: string;
  escrow: boolean;
  milestones: LoganSuggestedMilestone[];
  governingState?: string;
  additionalHomeownerName?: string;
  startDate?: string;
  substantialCompletionDate?: string;
  endDate?: string;
  projectCostModel?: string;
  allowances?: string;
  descriptionOfWork?: string;
  contractorAuthorizedRepresentative?: string;
  homeownerAuthorizedRepresentative?: string;
  contractorInsurance?: string;
  insuranceProviderName?: string;
  workersCompensationInsurance?: string;
  workersCompensationInsuranceLimit?: string;
  documentName?: string;
  sourceText: string;
  updatedAt: string;
}

const LEGACY_LOGAN_PROJECT_DRAFT_KEY = "loganProjectDraft.v1";
const LOGAN_PROJECT_DRAFT_KEY_PREFIX = "loganProjectDraft.v2.";
const LOGAN_PROJECT_SCOPE_KEY_PREFIX = "loganProjectScope.v1.";
const LOGAN_PROJECT_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const emptyLoganProjectDraft = (): LoganProjectDraft => ({
  projectName: "",
  scope: "",
  homeownerFirstName: "",
  homeownerLastName: "",
  homeownerEmail: "",
  homeownerPhone: "",
  homeownerId: null,
  address: "",
  city: "",
  state: "",
  zip: "",
  country: "US",
  costOfProject: "",
  escrow: false,
  milestones: [],
  sourceText: "",
  updatedAt: new Date().toISOString(),
});

export function loadLoganProjectDraft(): LoganProjectDraft | null {
  try {
    // Never expose an unscoped draft to a different account on a shared device.
    localStorage.removeItem(LEGACY_LOGAN_PROJECT_DRAFT_KEY);
    const stored = localStorage.getItem(getLoganProjectDraftKey());
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Partial<LoganProjectDraft>;
    if (!parsed || typeof parsed !== "object") return null;
    const updatedAt = Date.parse(String(parsed.updatedAt || ""));
    if (!Number.isFinite(updatedAt) || Date.now() - updatedAt > LOGAN_PROJECT_DRAFT_MAX_AGE_MS) {
      localStorage.removeItem(getLoganProjectDraftKey());
      return null;
    }
    return { ...emptyLoganProjectDraft(), ...parsed };
  } catch (_error) {
    return null;
  }
}

export function saveLoganProjectDraft(draft: LoganProjectDraft) {
  try {
    localStorage.setItem(getLoganProjectDraftKey(), JSON.stringify({
      ...draft,
      updatedAt: new Date().toISOString(),
    }));
  } catch (_error) {
    // The guided flow still works when browser storage is unavailable.
  }
}

export function clearLoganProjectDraft() {
  try {
    localStorage.removeItem(getLoganProjectDraftKey());
    localStorage.removeItem(LEGACY_LOGAN_PROJECT_DRAFT_KEY);
  } catch (_error) {
    // No cleanup is needed when browser storage is unavailable.
  }
}

function getLoganProjectDraftKey() {
  return `${LOGAN_PROJECT_DRAFT_KEY_PREFIX}${getCurrentDraftOwner()}`;
}

function getCurrentDraftOwner() {
  try {
    const owner = getAuthPayload().userId;
    return owner == null ? "anonymous" : String(owner).replace(/[^a-zA-Z0-9_-]/g, "");
  } catch (_error) {
    return "anonymous";
  }
}

export function saveLoganProjectScope(projectId: string | number, scope: string) {
  if (!projectId || !scope.trim()) return;
  try {
    sessionStorage.setItem(`${LOGAN_PROJECT_SCOPE_KEY_PREFIX}${projectId}`, scope.trim());
  } catch (_error) {
    // The project still works when session storage is unavailable.
  }
}

export function loadLoganProjectScope(projectId: string | number): string {
  if (!projectId) return "";
  try {
    return sessionStorage.getItem(`${LOGAN_PROJECT_SCOPE_KEY_PREFIX}${projectId}`) || "";
  } catch (_error) {
    return "";
  }
}

export function clearLoganProjectScope(projectId: string | number) {
  if (!projectId) return;
  try {
    sessionStorage.removeItem(`${LOGAN_PROJECT_SCOPE_KEY_PREFIX}${projectId}`);
  } catch (_error) {
    // No cleanup is needed when session storage is unavailable.
  }
}
